import React, { useRef, useEffect, useState } from "react";
import { useDispatch } from 'react-redux';

import {
    Dialog,
    Checkbox,
    Button,
    Typography,
    IconButton,
} from "@material-tailwind/react";
import SignaturePad from "react-signature-canvas";

import {
    Xmark
} from "iconoir-react";

import {
    DeliveryResponse,
    updatePartsRequest
} from "../redux/parts/actions";
import {
    Part,
} from "../redux/vehicles/actions";


interface DialogPartsDeliveryConfirmationProps {
    delivery: DeliveryResponse | null;
    isOpen: boolean;
    setIsOpen: (flag: any) => void;
}

const TABLE_HEAD = [
    {n: "Part Name", className: ""},
    {n: "Part Number", className: ""},
    {n: "Quantity", className: ""},
];

export const DialogPartsDeliveryConfirmation: React.FC<DialogPartsDeliveryConfirmationProps> = ({
    delivery,
    isOpen,
    setIsOpen,
}) => {
    const dispatch = useDispatch();
    const sigPadRef = useRef<SignaturePad>(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [step, setStep] = useState(0);
    const [selectedParts, setSelectedParts] = useState<number[]>([]);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        setError("");
    }, [selectedParts, isEmpty])

    useEffect(() => {
        //reset step
        setStep(0);
        setSelectedParts([]);
    }, [isOpen])

    const handleClear = () => {
        sigPadRef.current?.clear();
        setIsEmpty(true);
    };

    const handleConfirm = () => {
        if (0 == step) {
            if (selectedParts.length == 0) {
                setError("Please select at least one part");
            } else {
                setStep(1);
            }
        } else {
            if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
                const data = delivery?.parts.filter((item) => selectedParts.includes(item.id || 0)).map((item) => {
                    return {
                        ...item,
                        delivery_date: new Date().toISOString(),
                        signature: sigPadRef.current?.toDataURL("image/png"),
                    }
                });

                dispatch(updatePartsRequest({parts: data as Part[], update_fields: ['id', 'delivery_date', 'signature']}));

                setIsOpen(false);
            } else {
                setError("Please provide a signature.");
            }
        }
    };

    const handleCheckboxChange = (id: number) => {
        setSelectedParts((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const getTitle = () => {
        if (step) {
            return "Signature"
        } else {
            return "Confirm Parts"
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Overlay>
                <Dialog.Content className="p-2 md:p-8 max-w-lg">
                    <div className="text-center text-[#1F2937] mb-2">
                        <div className="flex w-[80%] items-center mx-auto justify-center gap-x-5">
                            <Button isPill={false} color="primary" className="h-8 w-8 text-center" onClick={() => setStep(0)}>
                                1
                            </Button>

                            <div className="h-[2px] grow bg-gray-200" />

                            <Button isPill={false} color={step==0 ? "secondary" : "primary"} disabled={step==0} className="h-8 w-8 text-center">
                                2
                            </Button>
                        </div>
                        <Typography as="h2" className="text-md font-bold">
                            {getTitle()}
                        </Typography>
                        <Dialog.DismissTrigger
                            as={IconButton}
                            size="sm"
                            variant="ghost"
                            color="secondary"
                            className="!absolute right-2 top-2"
                            isCircular
                        >
                            <Xmark className="h-5 w-5" />
                        </Dialog.DismissTrigger>
                    </div>

                    <div className="relative rounded h-64 border border-gray-300 bg-gray-50">
                        { step
                            ?  <SignaturePad
                                    ref={sigPadRef}
                                    onEnd={() => setIsEmpty(sigPadRef.current?.isEmpty() as boolean)}
                                    canvasProps={{
                                        className: "w-full h-full",
                                    }}
                                />
                            : <div className="w-full overflow-x-auto">
                                  <table className="w-full min-w-max table-auto text-left">
                                      <thead>
                                          <tr>
                                              {
                                                  TABLE_HEAD.map((head, idx:number) => (
                                                      <th key={idx} className={`border-b border-surface p-4 ${head.className}`}>
                                                          <Typography
                                                              type="small"
                                                              color="default"
                                                              className="block font-medium"
                                                          >
                                                              {head.n}
                                                          </Typography>
                                                      </th>
                                                  ))
                                              }
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {
                                              delivery?.parts.map((el: Part, idx: number) => (
                                                  <tr key={idx} className="group">
                                                      <td className="p-4 border-b border-surface group-last:border-none">
                                                          <div className="flex items-center gap-3">
                                                              <Checkbox
                                                                  id={`checkbox_${idx}`}
                                                                  checked={selectedParts.includes(el.id || 0)}
                                                                  onChange={() => handleCheckboxChange(el.id || 0)}
                                                              >
                                                                  <Checkbox.Indicator />
                                                              </Checkbox>
                                                              <Typography
                                                                  htmlFor={`checkbox_${idx}`}
                                                                  as="label"
                                                                  type="small"
                                                                  color="default"
                                                                  className="font-semibold w-20 whitespace-nowrap overflow-hidden text-ellipsis block"
                                                              >
                                                                  {el.name}
                                                              </Typography>
                                                          </div>
                                                      </td>
                                                      <td className="p-4 border-b border-surface group-last:border-none">
                                                          <Typography type="small" className="whitespace-nowrap overflow-hidden text-ellipsis block text-foreground">
                                                              {el.number || 'N/A'}
                                                          </Typography>
                                                      </td>
                                                      <td className="p-4 border-b border-surface group-last:border-none text-center">
                                                          <Typography type="small" className="whitespace-nowrap overflow-hidden w-30 text-center text-ellipsis block text-foreground">
                                                              {el.qty || 'N/A'}
                                                          </Typography>
                                                      </td>
                                                  </tr>
                                              ))
                                          }
                                      </tbody>
                                  </table>
                              </div>
                        }
                        {!!error &&
                            <p className="my-2 text-lg font-bold text-center text-red-500">
                                {error}
                            </p>
                        }
                    </div>

                    <div className="mt-5 flex justify-between">
                        <Button
                            onClick={handleClear}
                            size="sm"
                            variant="outline"
                            color="secondary"
                            className={`self-start justify-self-start ${step == 0 ? 'invisible' : ''}`}
                        >
                            Clear
                        </Button>
                        <div className="flex gap-2">
                            <Dialog.DismissTrigger
                                as={Button}
                                variant="ghost"
                                color="error"
                            >
                                Cancel
                            </Dialog.DismissTrigger>
                            <Button
                                color="primary"
                                onClick={handleConfirm}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
}
